import React from 'react'
import clsx from 'clsx'
import { Hierarchy, Rank } from 'iconsax-react'
import { useHistory } from 'react-router'
import { useAppSelector } from '@SonarCX/hooks/redux'
import { useTheme } from '@mui/material'

import Tooltip from '@SonarCX/components/atoms/Tooltip'

import { Routes } from '@SonarCX/Routes'
import { formatDateRelative } from '@SonarCX/utilities'
import { selectTaskPriority } from '@SonarCX/slices/priority'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useTaskList } from '@SonarCX/providers/TaskListProvider'
import useStyles from './styles'

type TaskRowProps = {
  tasks: (APITask & { status: string })[]
}

const TaskRow = ({ tasks }: TaskRowProps) => {
  const history = useHistory()
  const { priorities } = useAppSelector(selectTaskPriority)
  const theme = useTheme()

  const { allUsers, loadUser } = useUserAPI()
  const { setIDs } = useTaskList()

  const classes = useStyles()

  React.useEffect(() => {
    tasks.forEach(task => {
      if (task.assignee_ids.length > 0) {
        loadUser(task.assignee_ids[0])
      }
    })
  }, [tasks, loadUser])

  const handleClick = React.useCallback(
    async (task: APITask & { status: string }) => {
      const taskIDs = tasks.map(t => t.id)
      setIDs(taskIDs, task.id)
      history.push(`${Routes.TASKS}/${task.id}`)
    },
    [history, setIDs, tasks]
  )

  return [
    <div key='tasks-header' className={classes.headerContainer}>
      <div className={clsx(classes.grid, classes.taskItemHeader)}>
        {/* Empty div for icon */}
        <div />

        <div>Task Name</div>

        <div>Due Date</div>

        <div>Responsible</div>

        <div>Priority</div>
      </div>

      <div className={classes.separator}>
        <div />
      </div>
    </div>,

    ...tasks.map((task, i) => (
      <div
        key={`${task.id}-${i}`}
        onClick={() => handleClick(task)}
        className={clsx(classes.grid, classes.taskItemRow)}
      >
        <div className={classes.icon}>
          {task.parent_id ? (
            <Tooltip title='Subtask' arrow>
              <div>
                <Hierarchy size={theme.size.base} />
              </div>
            </Tooltip>
          ) : task.sub_tasks > 0 ? (
            <Tooltip title='Parent' arrow>
              <div>
                <Rank size={theme.size.base} />
              </div>
            </Tooltip>
          ) : null}
        </div>

        <div>{task.title}</div>

        <div>{formatDateRelative(task.due_date, true)}</div>

        <div>
          {task.assignee_ids.length > 0
            ? allUsers[task.assignee_ids[0]]?.name ?? 'Loading...'
            : 'No one'}
        </div>

        <div>
          {task.priority_id
            ? priorities.find(p => p.id === task.priority_id)?.name ??
              'Unknown priority'
            : 'No priority'}
        </div>
      </div>
    ))
  ]
}

export default TaskRow
